import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import { Form, FormInput, FormActions, FormTextArea, FormDate } from "../Controls/Form";
import TreeView from "../Controls/TreeView";
import SkillSearch from "../Controls/SkillSearch";
import API from "@src/API";
import { toast } from "react-toastify";

async function loadCategories() {
  const resp = await API.getAsync("/Skill/Categories");
  if (resp?.ok && Array.isArray(resp.jsonData)) {
    return resp.jsonData.map((node) => ({
      ID: node.ID ?? node.id,
      Parent: node.Parent ?? node.parent ?? 0,
      Name: node.Name ?? node.name ?? "",
    }));
  }
  return [];
}

export default function AdminSkillEdit() {
  const { id: idStr } = useParams();
  const skillID = Number.parseInt(idStr, 10) || 0;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [skill, updateSkill] = useState({
    ID: skillID,
    ParentID: 0,
    ParentSkillName: "",
    Name: "",
    ShortName: "",
    CategoryID: 0,
    CategoryName: "",
    Description: "",
    ReleaseDate: null,
    Version: "",
    WikiUrl: "",
    Status: "",
  });

  useEffect(() => {
    if (!skillID) return;
    (async () => {
      const resp = await API.getAsync("/Skill?ID=" + skillID);
      if (resp?.ok && resp.jsonData) {
        updateSkill({
          ...resp.jsonData,
          ParentID: resp.jsonData.ParentID || 0,
          CategoryID: resp.jsonData.CategoryID || 0,
        });
      } else {
        toast.error("Could not load skill");
      }
    })();
  }, [skillID]);

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...skill,
        ID: skill.ID || skillID || 0,
        ParentID: skill.ParentID > 0 ? skill.ParentID : 0,
      };
      const resp = await API.postAsync("/Skill", payload);
      if (resp?.ok) {
        toast.success("Skill saved");
        navigate("/admin");
      } else {
        toast.error("Save failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function onParentSelect(selected) {
    if (!selected) return;
    const id = selected.ID ?? selected.id ?? 0;
    const name = selected.Name ?? selected.name ?? "";
    updateSkill((prev) => ({
      ...prev,
      ParentID: id,
      ParentSkillName: name,
    }));
  }

  return (
    <div className="pagebody">
      <BreadCrumb props={[{ route: "/admin", label: "Admin" }, "Edit Skill"]} />
      <div className="tabbody">
        <Form object={skill} onUpdate={updateSkill}>
          <h2>{skill.Name || "Edit Skill"}</h2>
          <FormInput field="Name" label="Name" />
          <FormInput field="ShortName" label="Short Name" />
          <TreeView
            field="CategoryID"
            fieldDisplay="CategoryName"
            label="Category"
            getData={loadCategories}
            formObject={{
              object: skill,
              onChange: (value, field) => {
                updateSkill((prev) => ({ ...prev, [field]: value }));
              },
            }}
          />
          <FormTextArea field="Description" label="Description" />
          <FormDate field="ReleaseDate" label="Release Date" />
          <FormInput field="Version" label="Version" />
          <FormInput field="WikiUrl" label="Grokipedia URL" />
          <FormInput field="Status" label="Status" disabled />
          <div className="field">
            <label className="form-label">Parent Skill</label>
            <SkillSearch
              object={skill}
              field="ParentID"
              fieldDisplay="ParentSkillName"
              onSelect={onParentSelect}
            />
          </div>
          <FormActions
            doneLabel={busy ? "Saving…" : "Save"}
            onSaveAsync={async () => {
              await onDoneAsync();
            }}
            onCancelAsync={async () => {
              navigate("/admin");
            }}
          />
        </Form>
      </div>
    </div>
  );
}
